/**
 * Shared loader for small, customer-managed vocabularies that render as an option list rather than a
 * searchable entity picker - `SampleStatus` is the first of these.
 *
 * A vocabulary table holds tens of rows, is read in full, and stores a short code (not a database id)
 * on the record that references it, so the remote-search entity path is the wrong shape for it. An
 * input opts in through its descriptor:
 *
 *   PictForm.SelectOptionsEntity          - the entity name (e.g. 'SampleStatus').
 *   PictForm.SelectOptionsEntityValueField - the column persisted as the value (default ID<Entity>).
 *   PictForm.SelectOptionsEntityTextField  - the column shown as the label (default Name).
 *   PictForm.SelectOptionsEntitySortField  - the column the list is ordered by (default ID<Entity>).
 *   PictForm.SelectOptionsEntityFilter     - optional extra FoxHound stanza, ANDed into the read.
 *   PictForm.SelectOptionsEntityCap        - optional row cap (default 500).
 *
 * The load is shared per (entity + field config) across every input on the page. A customer with no
 * rows - and a read that fails - resolves to null, which every caller treats as "keep the static
 * PictForm.SelectOptions fallback", so an uncurated customer still gets a working list.
 *
 * @module _EntityOptionSource
 */

/** @typedef {{ id: any, text: string }} EntityOption */

const DEFAULT_ROW_CAP = 500;

/**
 * Cache key for one entity-backed option set. Inputs sharing an entity and field config share a load.
 *
 * @param {Object} pInput - The form input descriptor.
 * @return {string|null} - The cache key, or null when the input is not entity-backed.
 */
function getCacheKey(pInput)
{
	const tmpEntity = pInput && pInput.PictForm && pInput.PictForm.SelectOptionsEntity;
	if (!tmpEntity)
	{
		return null;
	}
	const tmpPictForm = pInput.PictForm;
	return [
		tmpEntity,
		tmpPictForm.SelectOptionsEntityValueField || `ID${tmpEntity}`,
		tmpPictForm.SelectOptionsEntityTextField || 'Name',
		tmpPictForm.SelectOptionsEntitySortField || `ID${tmpEntity}`,
		tmpPictForm.SelectOptionsEntityFilter || '',
		tmpPictForm.SelectOptionsEntityCap || DEFAULT_ROW_CAP,
	].join('|');
}

/**
 * The provider-scoped cache bag, created on first use.
 *
 * @param {Object} pProvider - The input-type provider (must expose `pict`).
 * @return {Record<string, {Loaded: boolean, Options: Array<EntityOption>, Callbacks: Array<Function>}>}
 */
function getCache(pProvider)
{
	if (!pProvider._entitySelectOptionCache)
	{
		pProvider._entitySelectOptionCache = {};
	}
	return pProvider._entitySelectOptionCache;
}

/**
 * Start the shared read for an entity-backed vocabulary. A failed read deletes the cache entry so the
 * next ask retries rather than being pinned to an empty list forever.
 *
 * @param {Object} pProvider - The input-type provider (must expose `pict`).
 * @param {Object} pInput - The form input descriptor.
 * @param {string} pCacheKey - The key from {@link getCacheKey}.
 * @return {void}
 */
function startLoad(pProvider, pInput, pCacheKey)
{
	const tmpCache = getCache(pProvider);
	const tmpPictForm = pInput.PictForm;
	const tmpEntity = tmpPictForm.SelectOptionsEntity;
	const tmpValueField = tmpPictForm.SelectOptionsEntityValueField || `ID${tmpEntity}`;
	const tmpTextField = tmpPictForm.SelectOptionsEntityTextField || 'Name';
	const tmpSortField = tmpPictForm.SelectOptionsEntitySortField || `ID${tmpEntity}`;
	const tmpCap = tmpPictForm.SelectOptionsEntityCap || DEFAULT_ROW_CAP;
	const tmpFilter = tmpPictForm.SelectOptionsEntityFilter ? `${tmpPictForm.SelectOptionsEntityFilter}~` : '';

	// meadow-endpoints pluralizes a scope by appending 's' - SampleStatus is served at SampleStatuss.
	pProvider.pict.HeadlightRestClient.getJSON(`${tmpEntity}s/FilteredTo/${tmpFilter}FSF~${tmpSortField}~ASC~0/0/${tmpCap}`, (pError, pRecords) =>
	{
		const tmpEntry = tmpCache[pCacheKey];
		if (!tmpEntry)
		{
			return;
		}
		if (pError || !Array.isArray(pRecords))
		{
			// Drop the entry so the next ask retries, but still release anyone waiting - an awaiting
			// caller (the Picker's DataProvider) would otherwise hang instead of using its fallback.
			pProvider.pict.log.warn(`EntityOptionSource: option load failed for [${tmpEntity}]: ${(pError && pError.message) || pError}`);
			delete tmpCache[pCacheKey];
			for (const fFailureCallback of tmpEntry.Callbacks)
			{
				try
				{
					fFailureCallback([]);
				}
				catch (pCallbackError)
				{
					pProvider.pict.log.error(`EntityOptionSource: repaint failed for [${tmpEntity}]: ${(pCallbackError && pCallbackError.message) || pCallbackError}`);
				}
			}
			return;
		}
		tmpEntry.Options = pRecords
			.filter((pRecord) => pRecord && (pRecord[tmpValueField] !== undefined) && (pRecord[tmpValueField] !== null) && (pRecord[tmpValueField] !== ''))
			.map((pRecord) => ({ id: pRecord[tmpValueField], text: pRecord[tmpTextField] || String(pRecord[tmpValueField]) }));
		tmpEntry.Loaded = true;
		const tmpCallbacks = tmpEntry.Callbacks;
		tmpEntry.Callbacks = [];
		for (const fCallback of tmpCallbacks)
		{
			try
			{
				fCallback(tmpEntry.Options);
			}
			catch (pCallbackError)
			{
				pProvider.pict.log.error(`EntityOptionSource: repaint failed for [${tmpEntity}]: ${(pCallbackError && pCallbackError.message) || pCallbackError}`);
			}
		}
	});
}

/**
 * Synchronously resolve the entity-backed option list, kicking off the shared load on first ask.
 *
 * @param {Object} pProvider - The input-type provider (must expose `pict`).
 * @param {Object} pInput - The form input descriptor.
 * @param {Function} [fOnLoaded] - Invoked once with the options after an in-flight load resolves.
 * @return {Array<EntityOption>|null} - The options, or null when the caller should use its static fallback.
 */
function resolveOptions(pProvider, pInput, fOnLoaded)
{
	const tmpCacheKey = getCacheKey(pInput);
	if (!tmpCacheKey || !pProvider || !pProvider.pict || !pProvider.pict.HeadlightRestClient)
	{
		return null;
	}
	const tmpCache = getCache(pProvider);
	const tmpEntry = tmpCache[tmpCacheKey];
	if (tmpEntry && tmpEntry.Loaded)
	{
		return (tmpEntry.Options.length > 0) ? tmpEntry.Options : null;
	}
	if (tmpEntry)
	{
		if (typeof fOnLoaded === 'function')
		{
			tmpEntry.Callbacks.push(fOnLoaded);
		}
		return null;
	}
	tmpCache[tmpCacheKey] = { Loaded: false, Options: [], Callbacks: (typeof fOnLoaded === 'function') ? [ fOnLoaded ] : [] };
	startLoad(pProvider, pInput, tmpCacheKey);
	return null;
}

/**
 * Promise form for callers (the Picker's async DataProvider) that can await the vocabulary. Resolves
 * to the static `PictForm.SelectOptions` fallback when the customer has no rows or the read failed.
 *
 * @param {Object} pProvider - The input-type provider (must expose `pict`).
 * @param {Object} pInput - The form input descriptor.
 * @return {Promise<Array<EntityOption>>} - The vocabulary, or the descriptor's static fallback.
 */
function resolveOptionsAsync(pProvider, pInput)
{
	const tmpFallback = (pInput && pInput.PictForm && Array.isArray(pInput.PictForm.SelectOptions)) ? pInput.PictForm.SelectOptions : [];
	return new Promise((fResolve) =>
	{
		const tmpResolved = resolveOptions(pProvider, pInput, (pOptions) =>
		{
			fResolve((pOptions && pOptions.length > 0) ? pOptions : tmpFallback);
		});
		if (Array.isArray(tmpResolved))
		{
			fResolve((tmpResolved.length > 0) ? tmpResolved : tmpFallback);
			return;
		}
		// Not entity-backed at all (no cache key / no REST client) - nothing will call back.
		if (!getCacheKey(pInput) || !pProvider || !pProvider.pict || !pProvider.pict.HeadlightRestClient)
		{
			fResolve(tmpFallback);
		}
	});
}

module.exports = { getCacheKey, resolveOptions, resolveOptionsAsync, DEFAULT_ROW_CAP };
